import { get } from '../api';
import { useLoad } from '../hooks';

interface Option {
  id: number;
  display_name: string;
  email?: string;
}

/**
 * Pick a person by role. Loads the role's active people once (a business has
 * dozens to a few hundred, so a plain select stays quick and accessible).
 * 0 means nobody chosen.
 */
export function ContactSelect(props: {
  role: 'customer' | 'client' | 'contractor' | 'referrer';
  value: number;
  onChange(id: number): void;
  /** Label for the empty choice; omit when a choice is required. */
  none?: string;
  /** Ids to leave out, e.g. team members already chosen. */
  exclude?: number[];
  /** Called with the chosen person (or null for the empty choice), so a form can use their details. */
  onPick?(o: { id: number; display_name: string; email: string } | null): void;
  /** Passed on to the select; `Field` adds these when it has an error or hint. */
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}) {
  const list = useLoad(
    () => get<{ items: Option[] }>(`/api/contacts?role=${props.role}&limit=200`),
    [props.role],
  );
  const options = (list.data?.items ?? []).filter(
    (o) => o.id === props.value || !(props.exclude ?? []).includes(o.id),
  );
  return (
    <select
      value={props.value}
      aria-invalid={props['aria-invalid']}
      aria-describedby={props['aria-describedby']}
      disabled={list.loading}
      onChange={(e) => {
        const id = Number(e.target.value);
        const option = options.find((o) => o.id === id);
        props.onChange(id);
        props.onPick?.(
          option
            ? { id: option.id, display_name: option.display_name, email: option.email ?? '' }
            : null,
        );
      }}
    >
      <option value={0}>{props.none ?? 'Choose…'}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.display_name}
        </option>
      ))}
    </select>
  );
}
